import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAppDispatch, useAppSelector } from '../../../hooks';
import {
  setDesiredLuminaireHeight,
  setHoveredLayer,
  setModelLayers,
} from '../../../store/reducers/workspace.reducer.ts';
import { getUser } from '../../../store/selectors/user.selector';
import { selectActiveVersion } from '../../../store/selectors/workspace.selector.ts';
import { isCalculationStarted } from '../../../utils/jobStatus.ts';
import {
  fromDisplayLength,
  getLengthInputStep,
  getLengthUnitLabel,
  toDisplayLength,
} from '../../../utils/unitSystem.ts';
import { NumericInput } from '../../common/NumericInput';
import { LayerTypeIcon } from '../../project-settings/components/LayerTypeIcon';
import { MAX_LUMINAIRE_HEIGHT_METERS, MIN_LUMINAIRE_HEIGHT_METERS } from './types';

const INPUT =
  'w-16 bg-black text-white px-1 py-0.5 rounded border border-gray-600 text-[12px] text-center tabular-nums focus:outline-none focus:ring-1 focus:ring-lime-500';

const clampHeight = (meters: number) =>
  Math.min(Math.max(meters, MIN_LUMINAIRE_HEIGHT_METERS), MAX_LUMINAIRE_HEIGHT_METERS);

interface MountingHeightsProps {
  /** Footer variant: the layer list is capped in height. */
  compact?: boolean;
}

/** Mounting height per CHL layer for autoposition, plus one field that sets them all. Used in the
 *  Luminaires section and in the footer's confirm step before autoposition runs. */
export function MountingHeights({ compact = false }: MountingHeightsProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const unitSystem = useSelector(getUser)!.settings.units;
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const desiredLuminaireHeight = useAppSelector((s) => s.workspace.desiredLuminaireHeight) ?? 8;
  const canModify = !isCalculationStarted(useAppSelector(selectActiveVersion));

  const chlLayers = useMemo(() => modelLayers.filter((l) => l.canHoldLuminaire), [modelLayers]);
  const unit = getLengthUnitLabel(unitSystem);
  const step = getLengthInputStep(unitSystem);
  const heightMin = toDisplayLength(MIN_LUMINAIRE_HEIGHT_METERS, unitSystem);
  const heightMax = toDisplayLength(MAX_LUMINAIRE_HEIGHT_METERS, unitSystem);

  const setLayerHeights = (meters: number, uuids?: string[]) => {
    const targets = new Set(uuids ?? chlLayers.map((l) => l.uuid));
    dispatch(
      setModelLayers(modelLayers.map((l) => (targets.has(l.uuid) ? { ...l, desiredLuminaireHeight: meters } : l))),
    );
  };

  const setAll = (meters: number) => {
    dispatch(setDesiredLuminaireHeight(meters));
    setLayerHeights(meters);
  };

  if (chlLayers.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[12px] font-medium text-gray-200">{t('workspace.panel.luminaires.heightsTitle')}</p>
        <label className="flex items-center gap-1.5 text-[11px] text-gray-400">
          {t('workspace.panel.luminaires.allLayers', { unit })}
          <NumericInput
            min={heightMin}
            max={heightMax}
            step={step}
            decimals={2}
            value={toDisplayLength(desiredLuminaireHeight, unitSystem)}
            disabled={!canModify}
            onValueChange={(v) => {
              const meters = fromDisplayLength(v, unitSystem);
              if (!isNaN(meters) && meters >= MIN_LUMINAIRE_HEIGHT_METERS && meters <= MAX_LUMINAIRE_HEIGHT_METERS) {
                setAll(meters);
              }
            }}
            onCommit={(v) => setAll(clampHeight(fromDisplayLength(v, unitSystem)))}
            className={INPUT}
          />
        </label>
      </div>
      <div className={`space-y-1 ${compact ? 'max-h-[148px] overflow-y-auto workspace-panel-scroll pr-0.5' : ''}`}>
        {chlLayers.map((layer) => (
          <div
            key={layer.uuid}
            className="flex items-center gap-2 h-8 px-2 rounded bg-white/[0.04]"
            onMouseEnter={() => dispatch(setHoveredLayer(layer.threeJsName))}
            onMouseLeave={() => dispatch(setHoveredLayer(null))}
          >
            <LayerTypeIcon type={layer.layerType} style={{ width: 14, height: 14, color: 'white' }} />
            <span className="flex-1 min-w-0 truncate text-[13px] text-white">{layer.displayName}</span>
            <NumericInput
              min={heightMin}
              max={heightMax}
              step={step}
              decimals={2}
              value={toDisplayLength(layer.desiredLuminaireHeight ?? desiredLuminaireHeight, unitSystem)}
              disabled={!canModify}
              onValueChange={(v) => {
                const meters = fromDisplayLength(v, unitSystem);
                if (!isNaN(meters)) setLayerHeights(clampHeight(meters), [layer.uuid]);
              }}
              className={INPUT}
            />
            <span className="w-4 text-[11px] text-gray-400">{unit}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
